import React, { useEffect, useState } from "react";
import { Button, Calendar, Col, Row, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
    addMonth,
    firstDayOfMonth,
    getAPIDateFormat,
    getDate,
    getDateMonthInReadable,
    getMonth,
    isTodayorAfter,
    lastDayOfMonth,
    subtractMonth
} from "../../utils/date.utils";
import { isValid, isValidArray } from "../../utils/utilities";
import { getSchedulesByIdForDates } from "../../actions/admin.action";
import { formateDataByDate, getActiveStatus } from "../schedule-components/render-tabs/render-tabs.module";
import { loadingAction } from "../../actions/loading-actions";
import './multi-day-fare-calendar.style.scss';

const { Text } = Typography;

const SCHEDULE_CLASS_MAP = {
    'active-schedule': 'mdfc-active',
    'disabled-api-schedule': 'mdfc-api-disabled',
    'blocked-schedule': 'mdfc-blocked',
    'not-active-status': 'mdfc-inactive'
};

const isClickableActive = (cellData) => {
    return isValid(cellData)
        && cellData.visible_outside == 1
        && cellData.is_active == 1;
};

export const MultiDayFareCalendar = ({
    scheduleId,
    travelDate,
    additionalDates = [],
    onAdditionalDatesChange,
    onReset
}) => {
    const dispatch = useDispatch();
    const travelMoment = moment(travelDate);
    const [currentDate, setCurrentDate] = useState(travelMoment);
    const [openCalenderData, setOpenCalenderData] = useState({});

    const travelApiDate = getAPIDateFormat(travelMoment);
    const currentMonth = getMonth(currentDate);

    useEffect(() => {
        if (!isValid(scheduleId)) {
            return;
        }
        const sqlStartDate = firstDayOfMonth(currentDate);
        const sqlEndDate = lastDayOfMonth(currentDate);
        dispatch(loadingAction(true));
        getSchedulesByIdForDates(
            scheduleId,
            sqlStartDate,
            sqlEndDate,
            (activeData) => {
                setOpenCalenderData(formateDataByDate(activeData));
                dispatch(loadingAction(false));
            },
            () => {
                dispatch(loadingAction(false));
            }
        );
    }, [scheduleId, currentMonth, dispatch]);

    const onPreviousClick = () => setCurrentDate(subtractMonth(currentDate, 1));
    const onNextClick = () => setCurrentDate(addMonth(currentDate, 1));

    const handleDateSelect = (date) => {
        const apidate = getAPIDateFormat(date);
        if (getMonth(date) !== currentMonth) {
            return;
        }
        if (apidate === travelApiDate) {
            return;
        }
        if (!isTodayorAfter(date)) {
            return;
        }
        const cellData = openCalenderData[apidate];
        if (!isClickableActive(cellData)) {
            return;
        }
        const next = [...additionalDates];
        const index = next.indexOf(apidate);
        if (index === -1) {
            next.push(apidate);
        } else {
            next.splice(index, 1);
        }
        onAdditionalDatesChange(next);
    };

    const renderDateCell = (date) => {
        const day = getDate(date);
        const month = getMonth(date);
        if (month !== currentMonth) {
            return (
                <div className="mdfc-other-month">
                    <div>{day}</div>
                </div>
            );
        }
        const apidate = getAPIDateFormat(date);
        const cellData = openCalenderData[apidate];
        const status = getActiveStatus(openCalenderData, apidate);
        const mappedClass = SCHEDULE_CLASS_MAP[status.className] || 'mdfc-inactive';
        const isTravel = apidate === travelApiDate;
        const isAdditional = !isTravel && additionalDates.indexOf(apidate) !== -1;
        const isPast = !isTodayorAfter(date);
        const clickable = isTravel || (!isPast && isClickableActive(cellData));

        let selectionMarker = '';
        if (isTravel) {
            selectionMarker = ' mdfc-travel-marker';
        } else if (isAdditional) {
            selectionMarker = ' mdfc-additional-marker';
        }
        const cursorClass = clickable ? '' : ' mdfc-not-clickable';

        return (
            <div className={`mdfc-cell ${mappedClass}${cursorClass}`} style={status.styles}>
                <div className={`mdfc-day${selectionMarker} ${status.onlyDateClassName || ''}`}>{day}</div>
            </div>
        );
    };

    const additionalCount = isValidArray(additionalDates) ? additionalDates.length : 0;
    const totalDays = 1 + additionalCount;

    return (
        <div className="multi-day-fare-calendar">
            <Row justify="space-between" align="middle" className="mdfc-summary-row">
                <Col>
                    <Text>
                        <strong>{totalDays} day{totalDays === 1 ? '' : 's'} total</strong>
                        {additionalCount > 0 && <span> ({additionalCount} additional)</span>}
                    </Text>
                </Col>
                <Col>
                    <Button
                        size="small"
                        className="mdfc-reset-btn"
                        onClick={onReset}
                        disabled={additionalCount === 0}
                    >
                        Reset
                    </Button>
                </Col>
            </Row>
            <div className="mdfc-calendar-wrapper">
                <Calendar
                    value={currentDate}
                    fullscreen={false}
                    headerRender={() => (
                        <Row gutter={[8, 8]} align="middle" justify="space-between" className="mdfc-header">
                            <Col>
                                <LeftOutlined className="mdfc-nav-icon" onClick={onPreviousClick} />
                            </Col>
                            <Col>
                                <Text strong>{getDateMonthInReadable(currentDate)}</Text>
                            </Col>
                            <Col>
                                <RightOutlined className="mdfc-nav-icon" onClick={onNextClick} />
                            </Col>
                        </Row>
                    )}
                    dateFullCellRender={renderDateCell}
                    onSelect={handleDateSelect}
                />
            </div>
            <Row gutter={[16, 8]} className="mdfc-legend">
                <Col>
                    <span className="mdfc-legend-swatch mdfc-legend-travel" /> Travel date
                </Col>
                <Col>
                    <span className="mdfc-legend-swatch mdfc-legend-additional" /> Additional day
                </Col>
                <Col>
                    <span className="mdfc-legend-swatch mdfc-legend-inactive" /> Inactive
                </Col>
            </Row>
        </div>
    );
};
